import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Loader2,
    Lock,
    FileText,
    Download,
    ArrowRight,
    TrendingUp,
    AlertTriangle,
    Sparkles,
} from "lucide-react";
import {
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import { toast } from "sonner";
import { api, extractError } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

const STATUS_BADGE = {
    draft: { label: "Draft", className: "bg-slate-100 text-slate-700 border-slate-200" },
    in_progress: { label: "In Progress", className: "bg-amber-50 text-amber-800 border-amber-200" },
    locked: { label: "Locked", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const OHI_BAND = (ohi) => {
    if (ohi == null) return { color: "#94A3B8", tint: "#F1F5F9" };
    if (ohi < 25) return { color: "#DC2626", tint: "#FEF2F2" };
    if (ohi < 50) return { color: "#D97706", tint: "#FFF7ED" };
    if (ohi < 75) return { color: "#1E56A0", tint: "#EEF4FB" };
    return { color: "#059669", tint: "#ECFDF5" };
};

const scoreBandColor = (s) => {
    if (s == null) return "#94A3B8";
    if (s <= 1.5) return "#DC2626";
    if (s <= 2.5) return "#D97706";
    if (s <= 3.5) return "#1E56A0";
    return "#059669";
};

async function downloadPdf(pathAndQuery, filenameHint) {
    try {
        const resp = await api.get(pathAndQuery, { responseType: "blob" });
        const cd = resp.headers?.["content-disposition"] || "";
        const match = /filename="?([^";]+)"?/.exec(cd);
        const filename = match?.[1] || filenameHint || "report.pdf";
        const url = URL.createObjectURL(resp.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    } catch (err) {
        toast.error(extractError(err, "Download failed"));
    }
}

export function ExecutiveDashboardPage() {
    const { cycleId } = useParams();
    const navigate = useNavigate();
    const [summary, setSummary] = useState(null);
    const [trends, setTrends] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pdfBusy, setPdfBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const { data: exec } = await api.get(`/cycles/${cycleId}/executive-summary`);
                setSummary(exec);
                if (exec?.organization?.id) {
                    try {
                        const { data: tr } = await api.get(`/organizations/${exec.organization.id}/trends`);
                        setTrends(tr);
                    } catch {
                        setTrends({ cycles: [] });
                    }
                }
                setError(null);
            } catch (e) {
                setError(extractError(e, "Could not load executive summary"));
            } finally {
                setLoading(false);
            }
        })();
    }, [cycleId]);

    const radarData = useMemo(() => {
        if (!summary) return [];
        return summary.domains.map((d) => ({
            domain: d.name.replace(/ &.*$/, "").replace(/ Systems.*/, ""),
            fullName: d.name,
            // Keep one decimal of precision — avoids showing floats like 54.99999…
            score: d.overall_score_0_100 == null ? 0 : Math.round(d.overall_score_0_100 * 10) / 10,
            active: d.is_active,
        }));
    }, [summary]);

    const trendsData = useMemo(() => {
        if (!trends || !trends.cycles) return [];
        return trends.cycles
            .filter((c) => c.ohi_score != null)
            .map((c) => ({
                name: c.name,
                lockedAt: (c.locked_at || "").slice(0, 10),
                OHI: c.ohi_score == null ? null : Math.round(c.ohi_score * 10) / 10,
                vision: c.domains?.vision == null ? null : Math.round(((c.domains.vision - 1) / 3) * 1000) / 10,
                curriculum: c.domains?.curriculum == null ? null : Math.round(((c.domains.curriculum - 1) / 3) * 1000) / 10,
                instructional: c.domains?.instructional == null ? null : Math.round(((c.domains.instructional - 1) / 3) * 1000) / 10,
            }));
    }, [trends]);

    if (loading) {
        return (
            <div className="text-[#0B2545] flex items-center gap-3" data-testid="exec-loading">
                <Loader2 className="animate-spin" size={18} /> Loading executive summary…
            </div>
        );
    }
    if (error || !summary) {
        return <div className="text-red-700" data-testid="exec-error">{error}</div>;
    }

    const ohi = summary.ohi_score;
    const band = OHI_BAND(ohi);
    const status = summary.cycle.status;
    const isLocked = status === "locked";
    const statusMeta = STATUS_BADGE[status] || STATUS_BADGE.draft;

    return (
        <div className="space-y-8" data-testid="executive-dashboard">
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <Link to={`/app/cycles/${cycleId}`} className="text-xs text-[#1E56A0] hover:underline">
                        ← Back to cycle
                    </Link>
                    <div className="mt-2 flex items-center gap-3 flex-wrap">
                        <h1
                            className="font-serif text-3xl sm:text-4xl font-semibold text-[#0B2545] leading-tight"
                            data-testid="exec-title"
                        >
                            Executive Summary
                        </h1>
                        <Badge className={statusMeta.className + " border"} variant="outline">
                            {isLocked ? <Lock size={12} className="mr-1" /> : null}
                            {statusMeta.label}
                        </Badge>
                    </div>
                    <p className="mt-2 text-sm text-[#0E1B2C]/70">
                        {summary.organization?.name} · {summary.cycle.name}
                    </p>
                </div>

                <div className="flex flex-wrap gap-2">
                    <Button
                        variant="outline"
                        onClick={() => navigate(`/app/cycles/${cycleId}/dashboard`)}
                        className="rounded-full border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-white"
                        data-testid="open-domain-dashboards-btn"
                    >
                        Domain dashboards <ArrowRight size={14} />
                    </Button>
                    <Button
                        onClick={async () => {
                            setPdfBusy(true);
                            await downloadPdf(`/cycles/${cycleId}/reports/full.pdf`, "ESSS_OrgReport.pdf");
                            setPdfBusy(false);
                        }}
                        disabled={pdfBusy}
                        className="rounded-full bg-[#0B2545] hover:bg-[#1E56A0] text-white"
                        data-testid="download-full-pdf-btn"
                    >
                        {pdfBusy ? <Loader2 className="animate-spin" size={14} /> : <FileText size={14} />}
                        Download full report (PDF)
                    </Button>
                </div>
            </div>

            {!isLocked && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-3 text-sm text-amber-900 flex items-start gap-2" data-testid="draft-banner">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                    <div>
                        This cycle is <strong>{statusMeta.label.toLowerCase()}</strong>. Any PDF you download will be watermarked <strong>DRAFT</strong> on every page until the cycle is locked.
                    </div>
                </div>
            )}

            {/* OHI Hero + Radar */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="lg:col-span-2 rounded-2xl overflow-hidden shadow-lg shadow-[#0B2545]/5"
                    data-testid="ohi-hero-card"
                >
                    <div className="p-6" style={{ background: `linear-gradient(140deg, ${band.tint}, #FFFFFF 70%)` }}>
                        <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0]">
                            Organizational Health Index
                        </div>
                        <div className="mt-2 flex items-end gap-3">
                            <div
                                className="font-serif font-semibold leading-none"
                                style={{ fontSize: "6rem", color: band.color }}
                                data-testid="ohi-score-number"
                            >
                                {ohi == null ? "—" : ohi.toFixed(1)}
                            </div>
                            <div className="pb-3 text-sm text-[#0E1B2C]/70">/ 100</div>
                        </div>
                        <div className="mt-2 font-serif text-lg text-[#0B2545]" data-testid="ohi-descriptor">
                            {summary.ohi_descriptor || "Not yet scored"}
                        </div>
                        <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                            <div className="rounded-lg bg-white/70 backdrop-blur border border-white p-3">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-[#1E56A0] font-bold">Findings</div>
                                <div className="mt-1 font-serif text-xl font-semibold text-[#0B2545]">
                                    {summary.findings_rollup.strength_count + summary.findings_rollup.gap_count + summary.findings_rollup.systems_misalignment_count + summary.findings_rollup.continuous_improvement_count}
                                </div>
                            </div>
                            <div className="rounded-lg bg-white/70 backdrop-blur border border-white p-3">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-[#1E56A0] font-bold">Red flags</div>
                                <div className="mt-1 font-serif text-xl font-semibold text-[#0B2545]">
                                    {summary.red_flags_total}
                                </div>
                            </div>
                            <div className="rounded-lg bg-white/70 backdrop-blur border border-white p-3">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-[#1E56A0] font-bold">Active domains</div>
                                <div className="mt-1 font-serif text-xl font-semibold text-[#0B2545]">
                                    {summary.domains.filter((d) => d.is_active).length}
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                <div
                    className="lg:col-span-3 rounded-2xl bg-white border border-[#D6E6F2] p-5"
                    data-testid="radar-chart-card"
                >
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0]">
                        Domain coherence radar
                    </div>
                    <div className="text-sm text-[#0E1B2C]/70 mt-1">
                        Score out of 100 per domain (weighted OHI shown at left).
                    </div>
                    <div className="mt-4 h-[320px]" data-testid="radar-chart">
                        <ResponsiveContainer width="100%" height="100%">
                            <RadarChart data={radarData} outerRadius="75%">
                                <PolarGrid stroke="#D6E6F2" />
                                <PolarAngleAxis
                                    dataKey="domain"
                                    tick={{ fill: "#0B2545", fontSize: 12, fontFamily: "Inter, sans-serif" }}
                                />
                                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "#64748B", fontSize: 10 }} />
                                <Radar
                                    name="Score"
                                    dataKey="score"
                                    stroke="#1E56A0"
                                    fill="#8AC4FF"
                                    fillOpacity={0.5}
                                />
                                <Tooltip
                                    formatter={(v, _n, item) => [
                                        `${(Number(v) || 0).toFixed(1)} / 100`,
                                        item?.payload?.fullName,
                                    ]}
                                    contentStyle={{ borderRadius: 8, border: "1px solid #D6E6F2" }}
                                />
                            </RadarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Per-domain summary */}
            <div className="rounded-2xl bg-white border border-[#D6E6F2] p-5" data-testid="domain-summary-card">
                <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0]">
                    Domain summary
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="domain-summary-grid">
                    {summary.domains.map((d) => (
                        <DomainSummaryCard
                            key={d.key}
                            cycleId={cycleId}
                            domain={d}
                            navigate={navigate}
                        />
                    ))}
                </div>
            </div>

            {/* Trends */}
            <div className="rounded-2xl bg-white border border-[#D6E6F2] p-5" data-testid="trends-card">
                <div className="flex items-center gap-2">
                    <TrendingUp className="text-[#1E56A0]" size={16} />
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#1E56A0]">
                        Trend history (locked cycles)
                    </div>
                </div>
                {trendsData.length === 0 ? (
                    <div className="mt-4 rounded-xl border border-dashed border-[#8AC4FF] bg-[#F1F6FB] px-4 py-8 text-center text-sm text-[#0E1B2C]/70" data-testid="trends-empty">
                        <Sparkles className="mx-auto text-[#1E56A0]" size={20} />
                        <div className="mt-2">No historical locked cycles yet. Lock a cycle to start plotting the OHI trend.</div>
                    </div>
                ) : (
                    <div className="mt-4 h-[300px]" data-testid="trends-chart">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={trendsData} margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E5EEF7" />
                                <XAxis dataKey="lockedAt" stroke="#0B2545" fontSize={11} />
                                <YAxis domain={[0, 100]} stroke="#0B2545" fontSize={11} />
                                <Tooltip
                                    contentStyle={{ borderRadius: 8, border: "1px solid #D6E6F2" }}
                                    formatter={(v) => (v == null ? "—" : (Number(v) || 0).toFixed(1))}
                                />
                                <Legend />
                                <Line type="monotone" dataKey="OHI" stroke="#0B2545" strokeWidth={3} dot={{ r: 4 }} />
                                <Line type="monotone" dataKey="vision" stroke="#2E5D3A" strokeWidth={1.5} dot={{ r: 3 }} strokeDasharray="4 4" />
                                <Line type="monotone" dataKey="curriculum" stroke="#3D2B7B" strokeWidth={1.5} dot={{ r: 3 }} strokeDasharray="4 4" />
                                <Line type="monotone" dataKey="instructional" stroke="#1B3A7B" strokeWidth={1.5} dot={{ r: 3 }} strokeDasharray="4 4" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </div>
    );
}

function DomainSummaryCard({ cycleId, domain, navigate }) {
    const isActive = domain.is_active;
    const [pdfBusy, setPdfBusy] = useState(false);
    const rating = domain.overall_rating;
    const score100 = domain.overall_score_0_100;

    return (
        <div
            className="rounded-2xl border border-[#D6E6F2] bg-white overflow-hidden hover:shadow-lg hover:shadow-[#0B2545]/5 transition-all"
            data-testid={`exec-domain-card-${domain.key}`}
        >
            <div className="h-1.5 w-full" style={{ backgroundColor: domain.color_hex }} />
            <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <div
                            className="text-[10px] uppercase tracking-[0.25em] font-bold"
                            style={{ color: domain.color_hex }}
                        >
                            {domain.weight_percent}% weight
                        </div>
                        <div className="font-serif text-lg text-[#0B2545] font-semibold mt-1 leading-snug">
                            {domain.name}
                        </div>
                    </div>
                    {!isActive && (
                        <Badge className="bg-slate-100 text-slate-600 border-slate-200 border shrink-0" variant="outline">
                            Placeholder
                        </Badge>
                    )}
                </div>

                {isActive && (
                    <div className="mt-3 flex items-center gap-4">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1E56A0]">Rating</div>
                            <div className="font-serif text-2xl font-semibold text-[#0B2545]">
                                {rating != null ? rating.toFixed(2) : "—"}
                                <span className="text-xs text-[#0E1B2C]/60 ml-1">/ 4.00</span>
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1E56A0]">Score</div>
                            <div className="font-serif text-2xl font-semibold" style={{ color: scoreBandColor(rating) }}>
                                {score100 != null ? score100.toFixed(1) : "—"}
                                <span className="text-xs text-[#0E1B2C]/60 ml-1">/ 100</span>
                            </div>
                        </div>
                    </div>
                )}

                {isActive && domain.descriptor && (
                    <div className="mt-2 text-sm text-[#0E1B2C]/75" data-testid={`exec-domain-descriptor-${domain.key}`}>
                        {domain.descriptor}
                    </div>
                )}

                {isActive && domain.top_recommendations?.length > 0 && (
                    <div className="mt-3 rounded-xl bg-[#F1F6FB] border border-[#D6E6F2] px-3 py-2">
                        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1E56A0]">Top recommendation</div>
                        <div className="text-sm text-[#0E1B2C] mt-1 line-clamp-2">
                            {domain.top_recommendations[0].text}
                        </div>
                    </div>
                )}

                {isActive && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        {domain.selected_red_flags_count > 0 && (
                            <Badge className="bg-red-50 text-red-700 border-red-200 border" variant="outline">
                                {domain.selected_red_flags_count} red flag{domain.selected_red_flags_count === 1 ? "" : "s"}
                            </Badge>
                        )}
                        {domain.auto_red_flags_count > 0 && (
                            <Badge className="bg-amber-50 text-amber-800 border-amber-200 border" variant="outline">
                                {domain.auto_red_flags_count} auto flag{domain.auto_red_flags_count === 1 ? "" : "s"}
                            </Badge>
                        )}
                    </div>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                    {isActive && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full text-xs"
                            onClick={() => navigate(`/app/cycles/${cycleId}/dashboard/${domain.key}`)}
                            data-testid={`exec-open-domain-${domain.key}`}
                        >
                            Open dashboard <ArrowRight size={12} />
                        </Button>
                    )}
                    {isActive && domain.da_id && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full text-xs border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-white"
                            disabled={pdfBusy}
                            onClick={async () => {
                                setPdfBusy(true);
                                await downloadPdf(
                                    `/domain-assessments/${domain.da_id}/report.pdf`,
                                    `ESSS_DomainReport_${domain.key}.pdf`,
                                );
                                setPdfBusy(false);
                            }}
                            data-testid={`exec-download-domain-pdf-${domain.key}`}
                        >
                            {pdfBusy ? <Loader2 className="animate-spin" size={12} /> : <Download size={12} />}
                            Domain PDF
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ExecutiveDashboardPage;
